import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  MapPin, Upload, Activity, Zap, Timer, ArrowUp, 
  Trash2, ChevronDown, ChevronUp, Info, Loader2,
  Navigation, Gauge, Heart, Map, Eye, TrendingUp, BarChart3
} from 'lucide-react';
import GPSMapVisualization from './GPSMapVisualization';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Device icons and colors
const DEVICE_CONFIG = {
  'Action Tracer': { color: '#22c55e', icon: '🏃' },
  'Playermaker': { color: '#3b82f6', icon: '⚽' },
  'Garmin': { color: '#f97316', icon: '⌚' },
  'Catapult': { color: '#a855f7', icon: '📡' },
  'STATSports': { color: '#ef4444', icon: '📊' },
  'OLIVER': { color: '#14b8a6', icon: '🤖' },
  'Unknown': { color: '#71717a', icon: '📍' }
};

export default function GPSTracking({ childId, childName }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [supportedDevices, setSupportedDevices] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [sessionPoints, setSessionPoints] = useState({});
  const [loadingPoints, setLoadingPoints] = useState(null);
  const [mapViewMode, setMapViewMode] = useState('route'); // 'route', 'heatmap', 'both'
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [sessionType, setSessionType] = useState('training');
  const [sport, setSport] = useState('football');
  const [deviceName, setDeviceName] = useState('');
  const [notes, setNotes] = useState('');

  // Load sessions and supported devices
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sessionsRes, devicesRes] = await Promise.all([
        axios.get(`${API}/gps-tracking/sessions/${childId}`),
        axios.get(`${API}/gps-tracking/supported-devices`)
      ]);
      setSessions(sessionsRes.data.sessions || []);
      setSupportedDevices(devicesRes.data.devices || []);
    } catch (error) {
      console.error('Failed to load GPS data:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load session points for map visualization
  const loadSessionPoints = useCallback(async (sessionId) => {
    if (sessionPoints[sessionId]) return; // Already loaded
    
    setLoadingPoints(sessionId);
    try {
      const response = await axios.get(`${API}/gps-tracking/session/${sessionId}?include_points=true`);
      setSessionPoints(prev => ({
        ...prev,
        [sessionId]: {
          points: response.data.points || [],
          heatmap: response.data.heatmap || []
        }
      }));
    } catch (error) {
      // Points might not be stored, that's okay
    } finally {
      setLoadingPoints(null);
    }
  }, [sessionPoints]);

  // Handle session expansion
  const handleExpandSession = (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
    } else {
      setExpandedSession(sessionId);
      loadSessionPoints(sessionId);
    }
  };

  // Handle file upload
  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('session_type', sessionType);
    formData.append('sport', sport);
    formData.append('device_name', deviceName || 'Unknown');
    if (notes) formData.append('notes', notes);

    try {
      const response = await axios.post(
        `${API}/gps-tracking/upload/${childId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`GPS data uploaded! ${response.data.summary?.points_count || 0} points processed`);
      setShowUploadForm(false);
      setUploadFile(null);
      setNotes('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload GPS data');
    } finally {
      setUploading(false);
    }
  };

  // Delete a session
  const handleDelete = async (sessionId) => {
    if (!window.confirm('Delete this GPS session?')) return;
    
    try {
      await axios.delete(`${API}/gps-tracking/session/${sessionId}`);
      toast.success('Session deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  // Format distance
  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${Math.round(meters)} m`;
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remainingMins = mins % 60;
      return `${hours}h ${remainingMins}m`;
    }
    return `${mins}m ${secs}s`;
  };

  // Format speed (m/s to km/h)
  const formatSpeed = (mps) => {
    if (!mps) return 'N/A';
    return `${(mps * 3.6).toFixed(1)} km/h`;
  };

  // Get device config
  const getDeviceConfig = (name) => {
    return DEVICE_CONFIG[name] || DEVICE_CONFIG['Unknown'];
  };

  if (loading) {
    return (
      <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin" />
          <span className="ml-3 text-zinc-400">Loading GPS data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="gps-tracking-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MapPin className="h-6 w-6 text-[#02b5ff]" />
          <h2 className="text-lg font-bold uppercase tracking-widest text-white">GPS Tracking</h2>
        </div>
        <Button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="h-10 px-6 bg-[#02b5ff] hover:bg-[#02b5ff]/80 text-white font-bold uppercase tracking-wider text-xs"
          data-testid="upload-gps-button"
        >
          <Upload className="mr-2 h-4 w-4" />
          Upload Data
        </Button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="mb-6 p-5 bg-zinc-900/50 border border-zinc-800 rounded-lg" data-testid="upload-form">
          <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-4">Upload GPS Session</h3>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* File Input */}
              <div className="md:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  GPS Data File
                </label>
                <input
                  type="file"
                  accept=".tcx,.gpx,.json,.csv"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 text-white text-sm rounded file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-[#02b5ff] file:text-white file:font-bold file:text-xs file:uppercase file:cursor-pointer"
                  data-testid="gps-file-input"
                />
                <p className="mt-1 text-xs text-zinc-500">Supported: TCX, GPX, JSON, CSV</p>
              </div>

              {/* Device Selection */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Device / Source
                </label>
                <select
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 text-white text-sm rounded"
                  data-testid="device-select"
                >
                  <option value="">Select device...</option>
                  {supportedDevices.map((device) => (
                    <option key={device.name} value={device.name}>
                      {device.name} ({device.manufacturer})
                    </option>
                  ))}
                </select>
              </div>

              {/* Session Type */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Session Type
                </label>
                <select
                  value={sessionType}
                  onChange={(e) => setSessionType(e.target.value)}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 text-white text-sm rounded"
                  data-testid="session-type-select"
                >
                  <option value="training">Training</option>
                  <option value="match">Match</option>
                  <option value="friendly">Friendly</option>
                  <option value="assessment">Assessment</option>
                </select>
              </div>

              {/* Sport */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Sport
                </label>
                <select
                  value={sport}
                  onChange={(e) => setSport(e.target.value)}
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 text-white text-sm rounded"
                  data-testid="sport-select"
                >
                  <option value="football">Football</option>
                  <option value="rugby">Rugby</option>
                  <option value="basketball">Basketball</option>
                  <option value="tennis">Tennis</option>
                  <option value="athletics">Athletics</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Notes (Optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., 5-a-side practice"
                  className="w-full p-3 bg-zinc-800 border border-zinc-700 text-white text-sm rounded placeholder:text-zinc-600"
                  data-testid="notes-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                disabled={uploading || !uploadFile}
                className="h-10 px-6 bg-green-600 hover:bg-green-500 text-white font-bold uppercase tracking-wider text-xs disabled:opacity-50"
                data-testid="submit-upload-button"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload
                  </>
                )}
              </Button>
              <Button
                type="button"
                onClick={() => setShowUploadForm(false)}
                variant="outline"
                className="h-10 px-6 border-zinc-700 text-zinc-400 hover:text-white font-bold uppercase tracking-wider text-xs"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Supported Devices Info */}
      <div className="mb-6 p-4 bg-[#02b5ff]/10 border border-[#02b5ff]/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-[#02b5ff] mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-[#02b5ff] mb-1">Supported GPS Devices</h4>
            <div className="flex flex-wrap gap-2 mt-2">
              {supportedDevices.slice(0, 6).map((device) => (
                <span 
                  key={device.name}
                  className="px-2 py-1 bg-zinc-800 text-zinc-300 text-xs rounded"
                  title={device.features?.join(', ')}
                >
                  {getDeviceConfig(device.name).icon} {device.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sessions List */}
      {sessions.length === 0 ? (
        <div className="text-center py-12 border-t border-zinc-800" data-testid="no-gps-sessions">
          <MapPin className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-4">No GPS sessions recorded yet</p>
          <p className="text-zinc-600 text-xs font-mono">
            Upload data from Action Tracer, Playermaker, Garmin, or any GPS device
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="sessions-list">
          {sessions.map((session) => {
            const deviceConfig = getDeviceConfig(session.device_name);
            const isExpanded = expandedSession === session.id;
            
            return (
              <div 
                key={session.id}
                className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden"
                data-testid={`session-${session.id}`}
              >
                {/* Session Header */}
                <div 
                  className="p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                  onClick={() => handleExpandSession(session.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                        style={{ backgroundColor: `${deviceConfig.color}20`, color: deviceConfig.color }}
                      >
                        {deviceConfig.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-bold capitalize">{session.session_type}</span>
                          <span className="text-zinc-500">•</span>
                          <span className="text-zinc-400 capitalize">{session.sport}</span>
                        </div>
                        <div className="text-xs text-zinc-500 font-mono mt-1">
                          {new Date(session.created_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })} • {session.device_name}
                        </div>
                      </div>
                    </div>
                    
                    {/* Quick Stats */}
                    <div className="flex items-center gap-6">
                      <div className="text-right hidden sm:block">
                        <div className="text-white font-mono font-bold">
                          {formatDistance(session.summary?.total_distance || 0)}
                        </div>
                        <div className="text-xs text-zinc-500">Distance</div>
                      </div>
                      <div className="text-right hidden md:block">
                        <div className="text-white font-mono font-bold">
                          {formatDuration(session.summary?.duration)}
                        </div>
                        <div className="text-xs text-zinc-500">Duration</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(session.id);
                          }}
                          className="p-2 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400 transition-colors"
                          title="Delete session"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-zinc-500" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-zinc-500" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-zinc-800 p-4 bg-zinc-900/30">
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-zinc-800/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Navigation className="h-4 w-4 text-[#02b5ff]" />
                          <span className="text-xs text-zinc-500 uppercase">Distance</span>
                        </div>
                        <div className="text-white font-mono font-bold">
                          {formatDistance(session.summary?.total_distance || 0)}
                        </div>
                      </div>
                      
                      <div className="bg-zinc-800/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Timer className="h-4 w-4 text-green-400" />
                          <span className="text-xs text-zinc-500 uppercase">Duration</span>
                        </div>
                        <div className="text-white font-mono font-bold">
                          {formatDuration(session.summary?.duration)}
                        </div>
                      </div>
                      
                      <div className="bg-zinc-800/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Gauge className="h-4 w-4 text-yellow-400" />
                          <span className="text-xs text-zinc-500 uppercase">Avg Speed</span>
                        </div>
                        <div className="text-white font-mono font-bold">
                          {formatSpeed(session.summary?.avg_speed)}
                        </div>
                      </div>
                      
                      <div className="bg-zinc-800/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Zap className="h-4 w-4 text-orange-400" />
                          <span className="text-xs text-zinc-500 uppercase">Max Speed</span>
                        </div>
                        <div className="text-white font-mono font-bold">
                          {formatSpeed(session.summary?.max_speed)}
                        </div>
                      </div>
                    </div>

                    {/* Additional Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="bg-zinc-800/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Activity className="h-4 w-4 text-purple-400" />
                          <span className="text-xs text-zinc-500 uppercase">Sprints</span>
                        </div>
                        <div className="text-white font-mono font-bold">
                          {session.summary?.sprint_count || 0}
                        </div>
                      </div>
                      
                      <div className="bg-zinc-800/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <ArrowUp className="h-4 w-4 text-red-400" />
                          <span className="text-xs text-zinc-500 uppercase">High Intensity</span>
                        </div>
                        <div className="text-white font-mono font-bold">
                          {formatDistance(session.summary?.high_intensity_distance || 0)}
                        </div>
                      </div>
                      
                      {session.summary?.avg_heart_rate && (
                        <div className="bg-zinc-800/50 p-3 rounded">
                          <div className="flex items-center gap-2 mb-1">
                            <Heart className="h-4 w-4 text-pink-400" />
                            <span className="text-xs text-zinc-500 uppercase">Avg HR</span>
                          </div>
                          <div className="text-white font-mono font-bold">
                            {session.summary.avg_heart_rate} bpm
                          </div>
                        </div>
                      )}
                      
                      <div className="bg-zinc-800/50 p-3 rounded">
                        <div className="flex items-center gap-2 mb-1">
                          <Map className="h-4 w-4 text-cyan-400" />
                          <span className="text-xs text-zinc-500 uppercase">GPS Points</span>
                        </div>
                        <div className="text-white font-mono font-bold">
                          {session.summary?.points_count?.toLocaleString() || 0}
                        </div>
                      </div>
                    </div>

                    {/* Distance Zones - Advanced Analytics */}
                    {session.summary?.distance_zones && (
                      <div className="mb-4 p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <BarChart3 className="h-5 w-5 text-[#02b5ff]" />
                          <span className="text-sm font-bold text-white uppercase">Distance Zones</span>
                        </div>
                        <div className="space-y-2">
                          {Object.entries(session.summary.distance_zones).map(([key, zone]) => (
                            <div key={key} className="flex items-center gap-3">
                              <div className="w-24 text-xs text-zinc-400">{zone.label}</div>
                              <div className="flex-1 h-4 bg-zinc-900 rounded-full overflow-hidden">
                                <div 
                                  className="h-full rounded-full transition-all"
                                  style={{ 
                                    width: `${zone.distance_percent || 0}%`,
                                    backgroundColor: zone.color
                                  }}
                                />
                              </div>
                              <div className="w-20 text-right text-xs font-mono text-white">
                                {formatDistance(zone.distance)}
                              </div>
                              <div className="w-12 text-right text-xs text-zinc-500">
                                {zone.distance_percent || 0}%
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Acceleration Metrics */}
                    {session.summary?.acceleration && session.summary.acceleration.acceleration_count > 0 && (
                      <div className="mb-4 p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg">
                        <div className="flex items-center gap-2 mb-3">
                          <TrendingUp className="h-5 w-5 text-orange-400" />
                          <span className="text-sm font-bold text-white uppercase">Acceleration Data</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className="text-center p-2 bg-zinc-900/50 rounded">
                            <div className="text-lg font-bold text-green-400">
                              {session.summary.acceleration.max_acceleration}
                            </div>
                            <div className="text-xs text-zinc-500">Max Accel (m/s²)</div>
                          </div>
                          <div className="text-center p-2 bg-zinc-900/50 rounded">
                            <div className="text-lg font-bold text-red-400">
                              {Math.abs(session.summary.acceleration.max_deceleration)}
                            </div>
                            <div className="text-xs text-zinc-500">Max Decel (m/s²)</div>
                          </div>
                          <div className="text-center p-2 bg-zinc-900/50 rounded">
                            <div className="text-lg font-bold text-[#02b5ff]">
                              {session.summary.acceleration.acceleration_count}
                            </div>
                            <div className="text-xs text-zinc-500">Accelerations</div>
                          </div>
                          <div className="text-center p-2 bg-zinc-900/50 rounded">
                            <div className="text-lg font-bold text-yellow-400">
                              {session.summary.acceleration.high_intensity_accel_count}
                            </div>
                            <div className="text-xs text-zinc-500">High Intensity</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* GPS Map Visualization */}
                    {(sessionPoints[session.id]?.points?.length > 0 || session.heatmap?.length > 0) && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Map className="h-5 w-5 text-[#02b5ff]" />
                            <span className="text-sm font-bold text-white uppercase">Route Map</span>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setMapViewMode('route')}
                              className={`px-3 py-1 text-xs rounded ${mapViewMode === 'route' ? 'bg-[#02b5ff] text-white' : 'bg-zinc-800 text-zinc-400'}`}
                            >
                              Route
                            </button>
                            <button
                              onClick={() => setMapViewMode('heatmap')}
                              className={`px-3 py-1 text-xs rounded ${mapViewMode === 'heatmap' ? 'bg-[#02b5ff] text-white' : 'bg-zinc-800 text-zinc-400'}`}
                            >
                              Heatmap
                            </button>
                            <button
                              onClick={() => setMapViewMode('both')}
                              className={`px-3 py-1 text-xs rounded ${mapViewMode === 'both' ? 'bg-[#02b5ff] text-white' : 'bg-zinc-800 text-zinc-400'}`}
                            >
                              Both
                            </button>
                          </div>
                        </div>
                        <GPSMapVisualization 
                          points={sessionPoints[session.id]?.points || []}
                          heatmap={session.heatmap || sessionPoints[session.id]?.heatmap || []}
                          viewMode={mapViewMode}
                          showSpeedColors={true}
                        />
                      </div>
                    )}

                    {/* Loading Points */}
                    {loadingPoints === session.id && (
                      <div className="mb-4 p-4 bg-zinc-800/30 border border-zinc-700 rounded flex items-center justify-center">
                        <Loader2 className="h-5 w-5 text-[#02b5ff] animate-spin mr-2" />
                        <span className="text-sm text-zinc-400">Loading map data...</span>
                      </div>
                    )}

                    {/* No Map Data */}
                    {!loadingPoints && !sessionPoints[session.id]?.points?.length && !session.heatmap?.length && (
                      <div className="mb-4 p-4 bg-zinc-800/30 border border-zinc-700 rounded text-center">
                        <Map className="h-8 w-8 text-zinc-600 mx-auto mb-2" />
                        <p className="text-sm text-zinc-400">No GPS route data available</p>
                        <p className="text-xs text-zinc-500 mt-1">
                          Upload files with GPS coordinates to see routes
                        </p>
                      </div>
                    )}

                    {/* Notes */}
                    {session.notes && (
                      <div className="mt-4 p-3 bg-zinc-800/30 border border-zinc-700 rounded">
                        <span className="text-xs text-zinc-500 uppercase">Notes:</span>
                        <p className="text-sm text-zinc-300 mt-1">{session.notes}</p>
                      </div>
                    )}

                    {/* File Info */}
                    <div className="mt-4 text-xs text-zinc-500 font-mono">
                      File: {session.filename} • Format: {session.file_format?.toUpperCase()}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
